import "./productRightSlider.css";
import { routes } from "../../routes";
import { useSelector } from "react-redux";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  getPriceDarkBgColor,
  getPriceLightBgColor,
} from "../../utils/HelperFunctions";

const ProductRightSlider = ({
  isCart,
  showOverlay,
  overlayData,
  setShowOverlay,
}) => {
  const startY = useRef(0);
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle mounting/unmounting with delay for animation
  useEffect(() => {
    if (showOverlay) {
      setShouldRender(true);
      setDragY(0);
      setIsDragging(false);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });

      document.body.style.overflow = "hidden";
    } else {
      setIsAnimating(false);

      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      document.body.style.overflow = "";

      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showOverlay]);

  // Swipe handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    startY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      e.preventDefault();
      const resistance = 0.4;
      setDragY(diff * resistance);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (dragY > 100) {
      setIsAnimating(false);
      setTimeout(() => {
        setShowOverlay(false);
        setDragY(0);
      }, 300);
    } else {
      setDragY(0);
    }
  };

  const closeOverlay = (e) => {
    e?.stopPropagation();
    setIsAnimating(false);
    setTimeout(() => {
      setShowOverlay(false);
      setDragY(0);
    }, 300);
  };

  const encryptData = (data) => {
    const base64EncodedData = window?.btoa(data?.toString());
    return base64EncodedData;
  };

  const handleClick = (item) => {
    closeOverlay();
    const boxNameEncrypt = encryptData(item?.slug);
    navigate(`${routes.BOXDETAIL}/${boxNameEncrypt}`);
  };

  if (!shouldRender) return null;

  const backdropOpacity = isMobile ? Math.max(0, 1 - dragY / 400) : 1;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`custom-drawer-backdrop ${isAnimating ? "active" : ""}`}
        style={{
          opacity: backdropOpacity,
          transition: isDragging ? "none" : "opacity 0.3s ease",
        }}
        onClick={closeOverlay}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`custom-drawer ${
          isDarkMode ? "drawer-dark" : "drawer-light"
        } ${isAnimating ? "drawer-open" : ""} ${
          isMobile ? "mobile hide-scrollbar" : ""
        } ${isAnimating ? "animating" : ""}`}
        style={
          isMobile
            ? {
                transform: isAnimating
                  ? `translate(-50%, calc(-50% + ${dragY}px))`
                  : "translate(-50%, -50%) scale(0.9)",
                opacity: isAnimating ? Math.max(0, 1 - dragY / 400) : 0,
                transition: isDragging
                  ? "none"
                  : "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
              }
            : {}
        }
      >
        {/* Swipe Handle - Only on mobile */}
        {isMobile && (
          <div
            className="swipe-handle-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            ref={(el) => {
              if (el)
                el.style.setProperty("background", "#1A1A1A", "important");
            }}
          >
            <div className="swipe-handle" />
          </div>
        )}

        {/* Header */}
        <div className="overlay-header">
          <div className="overlay-header-content">
            <h1 className="overlay-heading">
              {overlayData?.name || "Xbox Wireless Controller"}
            </h1>
            <div className="overlay-price">
              {overlayData?.formatted_price || "N/A"}
            </div>
          </div>
          <div className="overlay-close-btn" onClick={closeOverlay}>
            ✕
          </div>
        </div>

        {/* Main Two-Column Layout */}
        <div className="overlay-main-layout">
          {/* Left - Image Section */}
          <div
            style={{
              background: isDarkMode
                ? getPriceDarkBgColor(overlayData?.price)
                : getPriceLightBgColor(overlayData?.price),
            }}
            className="overlay-image-section"
          >
            <span className="overlay-percentage-outer">
              {overlayData?.pivot && (
                <span
                  className="overlay-percentage"
                  style={{
                    background: !isDarkMode
                      ? overlayData.price <= 20
                        ? "rgba(17, 0, 0, 0.2)"
                        : overlayData.price <= 50 && overlayData.price > 20
                          ? "rgba(169, 234, 241, 1)"
                          : overlayData.price <= 250 && overlayData.price > 50
                            ? "rgba(29, 76, 223, 0.2)"
                            : overlayData.price <= 2000 &&
                                overlayData.price > 250
                              ? "rgba(104, 65, 153, 0.25)"
                              : "rgba(208, 194, 141, 0.25)"
                      : "rgba(17, 0, 0, 0.45)",
                  }}
                >
                  {overlayData?.percentage}%
                </span>
              )}
            </span>
            <img src={overlayData?.image} alt={overlayData?.name} />
          </div>

          {/* Right - Content */}
          <div className="overlay-content">
            {/* Description */}
            <div className="overlay-section">
              <h2 className="overlay-section-title">Description</h2>
              <p className="overlay-section-text">
                {overlayData?.description || "N/A"}
              </p>
            </div>

            {!isCart && (
              <>
                {/* Outcome */}
                <div className="overlay-section">
                  <h2 className="overlay-section-title">Outcome</h2>
                  <p className="outcome-value">
                    {overlayData?.pivot?.min_limit}-
                    {overlayData?.pivot?.max_limit}
                  </p>
                </div>

                {/* Contained in boxes */}
                {overlayData?.boxes?.length > 0 && (
                  <div className="overlay-section">
                    <h2 className="overlay-section-title">
                      Contained in boxes
                    </h2>
                    <div className="contained-boxes">
                      {overlayData?.boxes
                        ?.filter((box) => !box?.hidden_from_shop)
                        ?.map((item, index) => (
                          <div
                            key={item.id || index}
                            className="box-item"
                            onClick={() => handleClick(item)}
                          >
                            <img
                              loading="lazy"
                              src={item?.box_images?.image}
                              alt={item?.name || `Box ${index + 1}`}
                            />
                            <span className="box-item-name">{item?.name}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductRightSlider;
